import { useAppSelector } from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import {
  OrderTypesKeysEnum,
  PAYMENT_METHOD,
  ReceiptItemStateEnum,
} from "../../../../utils/enums";
import { getFranchiseOrderType } from "../../../../utils/help/orderType/orderType";
import { PaidReceipt, RestaurantTypeEnum } from "../../../../utils/types";
import { selectCurrentDeliveryConfig } from "../../../../services/redux/selectors/deliverySelectors";
import { selectDeliveryType } from "../../../../services/redux/selectors/deliverySelectors";

type UseReceiptStateProps = {
  isOrdersTab: boolean;
  receipt: PaidReceipt;
};

export const useReceiptState = (props: UseReceiptStateProps) => {
  const { isOrdersTab, receipt } = props;
  const { t } = useTranslationI18();

  const franchiseConfig = useAppSelector(
    (state) => state.config.selectedFranchise?.franchiseConfig
  );
  const currentFranchiseDeliveryConfg = useAppSelector(
    selectCurrentDeliveryConfig
  );
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const ecommerce = useAppSelector(
    (state) => state.config?.restaurantInfo?.type
  );
  const deliveryType = useAppSelector(selectDeliveryType);

  const isEcommerce = ecommerce === RestaurantTypeEnum.ECOMMERCE;

  const franchiseOrderTypeConfig = getFranchiseOrderType(
    franchiseConfig,
    currentFranchiseDeliveryConfg
  );

  const confirmedShortly = t(
    isEcommerce ? "pay:confirmed-shortly-onlineStore" : "pay:confirmed-shortly"
  );
  const orderConfirmedUnpaidText = t("pay:order-confirmed-unpaid-text");
  const addressTo = t("delivery:address-to");
  const prepOnPayText = t("pay:prep-on-pay");
  const orderConfirmedText = t("pay:order-confirmed-text");
  const orderCompletedText = t("pay:order-completed-text");
  const pendingText = t("pay:pending-text");
  const receivedText = t("pay:received-text");
  const confirmedText = t("pay:confirmed-text");
  const readyText = t("pay:ready-text");
  const preparingI18 = t("pay:preparing");
  const orderCancelledI18 = t("pay:order-cancelled");
  const pendingPayment = t("pay:pending-payment");

  const workingOnYourOderI18 = t("pay:preparing");

  const paymentProcessing = t("pay:payment-processing");
  const preparingOrder = t("pay:we-are-preparing-your-order");
  const restaurantIsPreparingOrder = t("pay:restaurant-preparing-your-order");
  // Delivery orders without tracking (Roboost) never report leaving the branch,
  // so the furthest state we can show the guest is "preparing".
  const preparingStateText = isEcommerce
    ? preparingOrder
    : restaurantIsPreparingOrder;

  const receiptStateText = () => {
    const orderTypePayment = franchiseOrderTypeConfig[orderTypeParam || ""];

    switch (receipt.state) {
      case ReceiptItemStateEnum.PENDING:
        if (orderTypePayment?.prepOnPay) {
          return prepOnPayText;
        } else if (
          receipt.paymentMethod === PAYMENT_METHOD.ONLINE_CARD &&
          !receipt.paid_at
        ) {
          return paymentProcessing;
        } else {
          return confirmedShortly;
        }
      case ReceiptItemStateEnum.CONFIRMED:
        if (
          receipt.orderType === OrderTypesKeysEnum.DELIVERY ||
          receipt.orderType === OrderTypesKeysEnum.DELIVERYV2
        ) {
          return preparingStateText;
        } else {
          return receipt.paid_at === null
            ? orderConfirmedUnpaidText
            : orderConfirmedText;
        }
      case ReceiptItemStateEnum.COMPLETED:
        if (receipt.orderType === OrderTypesKeysEnum.DELIVERY) {
          return franchiseConfig?.delivery?.completedStatusTextPayScreen
            ? t(`pay:${franchiseConfig.delivery.completedStatusTextPayScreen}`)
            : preparingStateText;
        }

        if (receipt.orderType === OrderTypesKeysEnum.DELIVERYV2) {
          return currentFranchiseDeliveryConfg?.deliveryType
            ?.completedStatusTextPayScreen
            ? t(
                `delivery:${currentFranchiseDeliveryConfg.deliveryType.completedStatusTextPayScreen}`
              )
            : preparingStateText;
        }

        if (
          restaurantConfig?.settleBilleAndCompleteInOne &&
          receipt.paymentMethod === PAYMENT_METHOD.CASH
        ) {
          return t("pay:preparing-order");
        }

        return orderCompletedText;

      case ReceiptItemStateEnum.CANCELLED:
        return orderCancelledI18;
      default:
        return orderCompletedText;
    }
  };

  const isDeliveryType =
    receipt.orderType === OrderTypesKeysEnum.DELIVERY ||
    receipt.orderType === OrderTypesKeysEnum.DELIVERYV2;

  const showDeliveryAddress =
    receipt.orderType === OrderTypesKeysEnum.DELIVERY ||
    (receipt.orderType === OrderTypesKeysEnum.DELIVERYV2 &&
      (!isEcommerce ||
        (isEcommerce && receipt.state === ReceiptItemStateEnum.CONFIRMED)));

  const deliveryAddressText =
    receipt.note && receipt.note.length > 0
      ? receipt.note
      : `${receipt.deliveryReceipt?.address?.addressTitle}, ${receipt.deliveryReceipt?.address?.streetName}`;

  const progressClass = () => {
    if (receipt.state === ReceiptItemStateEnum.PENDING) {
      return "pending";
    }
    if (
      receipt.state === ReceiptItemStateEnum.CONFIRMED &&
      receipt.orderType !== OrderTypesKeysEnum.DELIVERY &&
      receipt.orderType !== OrderTypesKeysEnum.DELIVERYV2
    ) {
      return "confirmed";
    }

    return "ready";
  };

  const firstStepLabel =
    !(franchiseConfig?.roboostDelivery && deliveryType?.id === 1) &&
    isDeliveryType
      ? receivedText
      : pendingText;

  const secondStepLabel =
    isDeliveryType && !isEcommerce
      ? workingOnYourOderI18
      : restaurantConfig?.settleBilleAndCompleteInOne &&
        receipt.paymentMethod === PAYMENT_METHOD.CASH
      ? pendingPayment
      : confirmedText;

  const thirdStepLabel = restaurantConfig?.settleBilleAndCompleteInOne
    ? preparingI18
    : readyText;

  const secondStepActive =
    receipt.state === ReceiptItemStateEnum.CONFIRMED ||
    receipt.state === ReceiptItemStateEnum.COMPLETED;

  const thirdStepActive = receipt.state === ReceiptItemStateEnum.COMPLETED;

  const isCancelled = receipt.state === ReceiptItemStateEnum.CANCELLED;

  return {
    t,
    isOrdersTab,
    isEcommerce,
    franchiseConfig,
    receipt,
    receiptStateText,
    addressTo,
    showDeliveryAddress,
    deliveryAddressText,
    progressClass,
    firstStepLabel,
    secondStepLabel,
    thirdStepLabel,
    secondStepActive,
    thirdStepActive,
    isDeliveryType,
    isCancelled,
  };
};
